/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {EventEmitter} from "../events/EventEmitter";
import {IEventEmitter} from "../events/IEventEmitter";
import {ICartData} from "./CartData";
import {ICartMessage} from "./CartMessage";
import {ICartPosition} from "./CartPosition";
import {ICartPromotion} from "./CartPromotion";
import {ICartRebateVoucher} from "./CartRebateVoucher";
import {CurrencyCode} from "./CurrencyCode";
import {IMoney} from "./Money";
import {CartRequestHandler, ICartRequestHandlerPutCartOptions} from "./requestHandlers/CartRequestHandler";
import {ITax} from "./Tax";
import {GtinItem} from "../items/interface/Item.interface";

export class Cart {
  private cartData: ICartData;
  private readonly eventEmitter: IEventEmitter<ICartData>;
  private readonly shopId: number;

  constructor(shopId: number, cartData: ICartData) {
    this.shopId = shopId;
    this.cartData = cartData;
    this.eventEmitter = new EventEmitter<ICartData>();

    this.eventEmitter.on((payload) => {
      if (payload === this.cartData) {
        return;
      }
      this.cartData = payload;
    });

    this.addItem = this.addItem.bind(this) as (itemId: number, quantity?: number) => Promise<void>;
    this.addMultipleItems = this.addMultipleItems.bind(this) as (items: GtinItem[]) => Promise<void>;
    this.removeItem = this.removeItem.bind(this) as (itemId: number) => Promise<void>;
    this.addVoucher = this.addVoucher.bind(this) as (voucherCode: string) => Promise<void>;
    this.removeVoucher = this.removeVoucher.bind(this) as (voucherCode: string) => Promise<void>;
    this.put = this.put.bind(this) as (cartParams: ICartRequestHandlerPutCartOptions) => Promise<void>;
  }

  public get amountGross(): IMoney {
    return this.cartData.value.gross;
  }

  public get amountNet(): IMoney {
    return this.cartData.value.net;
  }

  public get cartId(): string {
    return this.cartData.cartId;
  }

  public get countryCode(): string {
    return this.cartData.countryCode;
  }

  public get currencyCode(): CurrencyCode {
    return this.cartData.currencyCode;
  }

  public get emitter(): IEventEmitter<ICartData> {
    return this.eventEmitter;
  }

  public get isCompleted(): boolean {
    return this.cartData.isCompleted;
  }

  public get isCorporateCustomer(): boolean {
    return this.cartData.isCorporateCustomer;
  }

  public get isNet(): boolean {
    return this.cartData.isNet;
  }

  public get isUsingPoints(): boolean {
    return this.cartData.isUsingPoints;
  }

  public get isValid(): boolean {
    return this.cartData.isValid;
  }

  public get logisticsTypeId(): number | null {
    return this.cartData.logisticsTypeId;
  }

  public get messages(): ICartMessage[] {
    return this.cartData.messages;
  }

  public get paymentInformation() {
    return this.cartData.paymentInformation;
  }

  public get positions(): ICartPosition[] {
    return this.cartData.positions;
  }

  public get promotion(): ICartPromotion | null {
    return this.cartData.promotion;
  }

  public get rebates(): ICartRebateVoucher[] {
    return this.cartData.rebates;
  }

  public get shippingInformation() {
    return this.cartData.shippingInformation;
  }

  public get taxAmounts(): ITax[] {
    return this.cartData.value.tax;
  }

  public get valuePayable(): IMoney {
    return this.cartData.value.payable;
  }

  public get valueRebate(): IMoney | undefined {
    return this.cartData.value.rebate;
  }

  public get valueSubtotal(): IMoney {
    return this.cartData.value.subtotal;
  }

  public get vatNumber(): string | null {
    return this.cartData.vatNumber;
  }

  public get visitorId(): string {
    return this.cartData.visitorId;
  }

  /**
   * Adds an item to the cart
   * @param {number} itemId
   * @param {number} quantity
   * @returns {Promise<void>}
   * @throws {CartError | ApiError}
   */
  public async addItem(this: Cart, itemId: number, quantity = 1): Promise<void> {
    this.cartData = await CartRequestHandler.addItem(this.shopId, this.cartId, itemId, quantity);
    this.eventEmitter.emit(this.cartData);
  }

  /**
   * Adds multiple items to the cart
   * @param {GtinItem} items
   * @returns {Promise<void>}
   * @throws {CartError | ApiError}
   */
  public async addMultipleItems(this: Cart, items: GtinItem[]): Promise<void> {
    this.cartData = await CartRequestHandler.addMultipleItems(this.shopId, this.cartId, items);
    this.eventEmitter.emit(this.cartData);
  }

  /**
   * Adds a voucher to the cart
   * @param {string} voucherCode
   * @returns {Promise<void>}
   * @throws {CartError | ApiError}
   */
  public async addVoucher(this: Cart, voucherCode: string): Promise<void> {
    this.cartData = await CartRequestHandler.addVoucher(this.shopId, this.cartId, voucherCode);
    this.eventEmitter.emit(this.cartData);
  }

  public async get(this: Cart): Promise<void> {
    this.cartData = await CartRequestHandler.getCart(this.shopId, this.cartId);
    this.eventEmitter.emit(this.cartData);
  }

  public async put(this: Cart, cartParams: ICartRequestHandlerPutCartOptions): Promise<void> {
    this.cartData = await CartRequestHandler.putParams(this.shopId, this.cartId, cartParams);
    this.eventEmitter.emit(this.cartData);
  }

  /**
   * Removes an item from the cart
   * @param {number} itemId
   * @returns {Promise<void>}
   * @throws {CartError | ApiError}
   */
  public async removeItem(this: Cart, itemId: number): Promise<void> {
    this.cartData = await CartRequestHandler.removeItem(this.shopId, this.cartId, itemId);
    this.eventEmitter.emit(this.cartData);
  }

  /**
   * Removes a voucher from the cart
   * @param {string} voucherCode
   * @returns {Promise<void>}
   * @throws {CartError | ApiError}
   */
  public async removeVoucher(this: Cart, voucherCode: string): Promise<void> {
    this.cartData = await CartRequestHandler.removeVoucher(this.shopId, this.cartId, voucherCode);
    this.eventEmitter.emit(this.cartData);
  }
}
